// frontend/src/utils/engagementBeacon.js
//
// Anonymous page-engagement tracker: how long a visitor's tab was VISIBLE on a
// page, how long each measured section held the middle of the screen, and how
// far down the page they got. One cumulative snapshot per page view, POSTed to
// the first-party `POST /analytics/page-engagement` (no auth, always 204).
//
// WHO IS MEASURED. Anonymous visitors only, and only once the auth check has
// resolved - AnalyticsContext decides that, with the same `loading`/`user`
// gating and token-path skip as the page-view beacon, and calls
// `trackEngagementPage` / `discardEngagement`. Nothing here knows about auth.
//
// WHICH PAGES. Public marketing pages only (utils/engagementPages.js, mirrored
// by the backend's ENGAGEMENT_PAGES, which drops anything else). On any other
// path - sign-up, login, an account page, a legal page, an unknown path - the
// current page view is DISCARDED, never sent: leaving the homepage for the
// sign-up page must not put a snapshot on the wire moments before an account
// appears.
//
// WHAT IS NEVER COLLECTED OR STORED. No session id, no cookie, no user id,
// nothing written to localStorage/sessionStorage/IndexedDB. The page-view id
// `pv` is random, lives in MEMORY for one page view only, and exists so the
// backend can keep the largest of several cumulative snapshots of the same
// view. The request goes out with `credentials: 'omit'`, which is why this is a
// bare fetch and NOT navigator.sendBeacon (sendBeacon always attaches cookies,
// and in cookie-auth mode that would put the session cookie on the wire).
//
// ONE MODULE-LEVEL SINGLETON. Start is idempotent per path and React cleanup
// never sends: under StrictMode the effect runs twice for one mount, and the
// second call for the same path is a no-op. A send happens only on a real
// boundary - the tab hidden (the primary signal; iOS never fires pagehide
// reliably), pagehide, or an SPA path change between two measured pages (the
// PREVIOUS page view is flushed before the next starts). The auth flip, a
// token-bearing path and an unmeasured path are NOT send boundaries: the
// unsent page view is dropped (`discardEngagement`), so nothing goes out at the moment a visitor logs in or
// signs up (a beacon at that instant could be lined up with the new account
// by time); snapshots already sent on an earlier hide stay. No unload /
// beforeunload listener, ever: either one disqualifies the page from the
// back/forward cache. A page restored from that cache (`pageshow` with
// `persisted`) resumes the SAME pv.
//
// HOW TIME IS ATTRIBUTED. A 1 Hz tick runs only while the tab is visible. Each
// tick adds 1s to the page, 1s to the ONE non-overlay section whose box spans
// the viewport's vertical middle, and 1s to EVERY overlay section currently
// rendered and on screen (counted in parallel - a popup over the pricing table
// is time on both). The names are gathered into ONE Set per tick, so no name
// ever gains more than 1s in a tick. The scan is a getBoundingClientRect pass
// over the tagged elements on each tick, not an IntersectionObserver: sections
// in lazy chunks mount late and an observer registered at start would never
// see them.
//
// FAILURE IS SILENT. Every DOM read, the fetch and the consent read are
// guarded; analytics must never break or slow a public page.

import { getAcquisitionCodes } from "./adVisitor";
import { isEngagementPage, normalizeEngagementPath } from "./engagementPages";
import { OVERLAY_ATTR, SECTION_ATTR, isEngagementSection } from "./engagementSections";

/**
 * Owner decision 2026-10-19: measure EVERYONE, no consent gate, under the
 * lawyer-approved cookie-policy clause (section 2). Flipping this to `true` makes the
 * tracker send only when analytics consent is granted (the caller supplies the
 * check - AnalyticsContext passes the shared stored-consent reader). The
 * measuring itself is in memory and continues either way; only the send is
 * gated.
 */
export const ENGAGEMENT_REQUIRES_CONSENT = false;

/** Endpoint path under the API base (the page-view beacon's base). */
export const ENGAGEMENT_ENDPOINT_PATH = "/analytics/page-engagement";

/** The backend's cap on `secs` (contract C): an hour of visible time. */
export const MAX_ENGAGEMENT_SECS = 3600;

/** Tick period. 1 Hz is the resolution the admin card reports in. */
export const ENGAGEMENT_TICK_MS = 1000;

// Same derivation as services/api.js's axios instance, read directly so this
// module never loads the axios chain (the page-view beacon reads
// `api.defaults.baseURL`, which is this same value).
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

// Contract C's page_path rule: starts with "/", no "?", "#", whitespace or
// control characters, at most 255 characters.
// eslint-disable-next-line no-control-regex
const PAGE_PATH_RE = /^\/[^\s?#\u0000-\u001f\u007f]{0,254}$/;

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

/**
 * A random page-view id matching `^[A-Za-z0-9-]{8,40}$`. In memory only.
 *
 * crypto.randomUUID first (36 chars); then 16 random bytes as hex; then, on a
 * runtime with no Web Crypto at all, a Math.random string - fine for an id
 * whose only job is to tell two page views apart.
 */
export function generatePageViewId() {
  try {
    const c = typeof window !== "undefined" ? window.crypto : undefined;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    if (c && typeof c.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      c.getRandomValues(bytes);
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch (error) {
    /* fall through to the non-crypto id */
  }
  let id = "";
  while (id.length < 24) id += Math.random().toString(36).slice(2);
  return id.slice(0, 24);
}

/**
 * Scroll depth as a multiple of 10 in 0..100: the share of the page whose
 * bottom edge the viewport has reached. A page that fits the viewport is 100.
 *
 * @param {number} scrollY
 * @param {number} viewportHeight
 * @param {number} scrollHeight
 * @returns {number}
 */
export function scrollDepthPct(scrollY, viewportHeight, scrollHeight) {
  if (!Number.isFinite(scrollHeight) || scrollHeight <= 0) return 0;
  const reached = (Number(scrollY) || 0) + (Number(viewportHeight) || 0);
  const pct = Math.floor((reached / scrollHeight) * 10) * 10;
  if (!Number.isFinite(pct)) return 0;
  return Math.min(100, Math.max(0, pct));
}

/**
 * The allowlisted, NON-overlay section whose box spans the viewport's vertical
 * middle, or null (the middle can fall in a gap - the ticker strip, the footer).
 *
 * @param {Element[]} elements tagged elements
 * @param {number} viewportHeight
 * @returns {string|null}
 */
export function pickMiddleSection(elements, viewportHeight) {
  const middle = viewportHeight / 2;
  for (const el of elements) {
    if (el.hasAttribute(OVERLAY_ATTR)) continue;
    const name = el.getAttribute(SECTION_ATTR);
    if (!isEngagementSection(name)) continue;
    const rect = el.getBoundingClientRect();
    if (rect.height > 0 && rect.top <= middle && rect.bottom > middle) return name;
  }
  return null;
}

/**
 * Every allowlisted OVERLAY section currently rendered and on screen, each name
 * once (two elements of one name are one second, not two).
 *
 * @param {Element[]} elements tagged elements
 * @param {number} viewportWidth
 * @param {number} viewportHeight
 * @returns {string[]}
 */
export function visibleOverlaySections(elements, viewportWidth, viewportHeight) {
  const names = new Set();
  for (const el of elements) {
    if (!el.hasAttribute(OVERLAY_ATTR)) continue;
    const name = el.getAttribute(SECTION_ATTR);
    if (!isEngagementSection(name) || names.has(name)) continue;
    const rect = el.getBoundingClientRect();
    const onScreen =
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < viewportHeight &&
      rect.left < viewportWidth;
    if (!onScreen) continue;
    // An overlay held in the DOM but hidden (a closing popup) is not on screen.
    try {
      if (window.getComputedStyle(el).visibility === "hidden") continue;
    } catch (error) {
      /* no computed style - trust the box */
    }
    names.add(name);
  }
  return Array.from(names);
}

/**
 * The acquisition codes of a page view, read ONCE when it starts (adVisitor.js:
 * sessionStorage first, then the address bar, each validated with
 * CaptureAcquisition's pattern - an invalid value is absent, never sent).
 *
 * @returns {{s: string|null, c: string|null}}
 */
function snapshotAcquisitionCodes() {
  try {
    return getAcquisitionCodes();
  } catch (error) {
    return { s: null, c: null };
  }
}

/**
 * The request body for one snapshot - exactly contract C's fields. Every
 * section value is clamped to `secs` (the backend 422s a section held longer
 * than the page), whatever the accounting upstream did.
 *
 * @param {{path: string, pv: string, scroll: number, secs: number, sec: object}} view
 * @param {{s?: string|null, c?: string|null}} codes
 */
export function buildEngagementPayload(view, codes = {}) {
  const sec = {};
  for (const [name, seconds] of Object.entries(view.sec || {})) {
    sec[name] = Math.min(seconds, view.secs);
  }
  const body = {
    page_path: view.path,
    pv: view.pv,
    scroll: view.scroll,
    secs: view.secs,
    sec,
  };
  if (codes.s) body.s = codes.s;
  if (codes.c) body.c = codes.c;
  return body;
}

// ---------------------------------------------------------------------------
// The singleton
// ---------------------------------------------------------------------------

/** The page view being measured, or null when the tracker is stopped. */
let view = null;
let consentCheck = null;
let tickTimer = null;
let scrollFrame = 0;
let listening = false;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

// The prerender build snapshots public pages in a headless browser; that bot's
// "visit" must never be counted.
const isPrerender = () => isBrowser() && window.__PRERENDER__ === true;

const isVisible = () => {
  try {
    return document.visibilityState === "visible";
  } catch (error) {
    return false;
  }
};

const newView = (path) => ({
  path,
  pv: generatePageViewId(),
  codes: snapshotAcquisitionCodes(),
  secs: 0,
  sec: {},
  scroll: 0,
  sentSecs: -1,
  sentScroll: -1,
});

function sampleScroll() {
  if (!view) return;
  try {
    const root = document.documentElement;
    const scrollHeight = Math.max(
      root ? root.scrollHeight : 0,
      document.body ? document.body.scrollHeight : 0
    );
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const pct = scrollDepthPct(scrollY, window.innerHeight, scrollHeight);
    if (pct > view.scroll) view.scroll = pct;
  } catch (error) {
    /* unreadable layout - keep the max we have */
  }
}

function tick() {
  if (!view || !isVisible()) return;
  // At the cap nothing more is counted - which also keeps every section value
  // at or below `secs`, the backend's per-key rule.
  if (view.secs >= MAX_ENGAGEMENT_SECS) return;
  view.secs += 1;
  try {
    const elements = Array.from(document.querySelectorAll(`[${SECTION_ATTR}]`));
    // ONE set per tick: a name tagged both in-flow and as an overlay is still
    // one second, never two.
    const names = new Set(visibleOverlaySections(elements, window.innerWidth, window.innerHeight));
    const middle = pickMiddleSection(elements, window.innerHeight);
    if (middle) names.add(middle);
    for (const name of names) {
      view.sec[name] = (view.sec[name] || 0) + 1;
    }
  } catch (error) {
    /* a DOM read failed - the page second still counts */
  }
  sampleScroll();
}

function startTicking() {
  if (tickTimer !== null || !view || !isVisible()) return;
  tickTimer = window.setInterval(tick, ENGAGEMENT_TICK_MS);
}

function stopTicking() {
  if (tickTimer === null) return;
  window.clearInterval(tickTimer);
  tickTimer = null;
}

function consentAllowsSend() {
  if (!ENGAGEMENT_REQUIRES_CONSENT) return true;
  try {
    return typeof consentCheck === "function" && consentCheck() === true;
  } catch (error) {
    return false;
  }
}

/**
 * Send the current page view's cumulative snapshot, when there is something
 * new to say. Under a second is not a view worth a row; an unchanged snapshot
 * (hidden, then pagehide a moment later) is not worth a second request.
 */
function flush() {
  if (!view || view.secs < 1) return;
  if (view.secs === view.sentSecs && view.scroll === view.sentScroll) return;
  if (!consentAllowsSend() || isPrerender()) return;
  try {
    if (typeof fetch !== "function") return;
    const body = buildEngagementPayload(view, view.codes);
    view.sentSecs = view.secs;
    view.sentScroll = view.scroll;
    // Fire and forget. keepalive lets it outlive the page it describes.
    fetch(`${API_BASE_URL}${ENGAGEMENT_ENDPOINT_PATH}`, {
      method: "POST",
      keepalive: true,
      credentials: "omit",
      // The page URL (path and any query) never rides along as a Referer.
      referrerPolicy: "no-referrer",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch (error) {
    /* fetch unavailable or threw synchronously - analytics never breaks a page */
  }
}

function onVisibilityChange() {
  if (isVisible()) {
    startTicking();
  } else {
    stopTicking();
    flush();
  }
}

function onPageHide() {
  stopTicking();
  flush();
}

function onPageShow(event) {
  // Restored from the back/forward cache: same document, same pv, resume.
  if (event && event.persisted) startTicking();
}

function onScroll() {
  if (scrollFrame) return;
  if (typeof window.requestAnimationFrame !== "function") {
    sampleScroll();
    return;
  }
  scrollFrame = window.requestAnimationFrame(() => {
    scrollFrame = 0;
    sampleScroll();
  });
}

function cancelScrollFrame() {
  if (scrollFrame && typeof window.cancelAnimationFrame === "function") {
    window.cancelAnimationFrame(scrollFrame);
  }
  scrollFrame = 0;
}

function attachListeners() {
  if (listening) return;
  listening = true;
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("scroll", onScroll, { passive: true });
}

function detachListeners() {
  if (!listening) return;
  listening = false;
  document.removeEventListener("visibilitychange", onVisibilityChange);
  window.removeEventListener("pagehide", onPageHide);
  window.removeEventListener("pageshow", onPageShow);
  window.removeEventListener("scroll", onScroll, { passive: true });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Measure `path` as the current page view - when it is a measured marketing
 * page (utils/engagementPages.js; a trailing slash is ignored).
 *
 * Idempotent for the same page (StrictMode's double effect, a re-render). A
 * DIFFERENT measured page flushes the previous page view first, then starts a
 * new one with a fresh pv. Any other path - unmeasured, or outside contract
 * C's shape - DISCARDS the current page view unsent and measures nothing.
 *
 * @param {string} path the (already token-masked) pathname
 * @param {{hasConsent?: () => boolean}} [options] the consent check, used only
 *   while ENGAGEMENT_REQUIRES_CONSENT is true
 */
export function trackEngagementPage(path, options = {}) {
  try {
    if (!isBrowser() || isPrerender()) return;
    if (typeof options.hasConsent === "function") consentCheck = options.hasConsent;
    const page = normalizeEngagementPath(path);
    if (!page || !PAGE_PATH_RE.test(page) || !isEngagementPage(page)) {
      discardEngagement();
      return;
    }
    if (view && view.path === page) return;
    if (view) flush();
    cancelScrollFrame();
    view = newView(page);
    attachListeners();
    stopTicking();
    startTicking();
  } catch (error) {
    /* never break navigation */
  }
}

/**
 * Stop measuring and DROP the current page view without sending it - the
 * visitor just logged in / signed up, or moved to a token-bearing or
 * unmeasured path. A
 * snapshot already sent on an earlier hide stays; nothing new goes out. Safe
 * to call when already stopped.
 */
export function discardEngagement() {
  try {
    if (!isBrowser()) return;
    stopTicking();
    cancelScrollFrame();
    detachListeners();
    view = null;
  } catch (error) {
    /* never break navigation */
  }
}

/** Tests only: a copy of the live page view, or null. */
export function __getEngagementViewForTests() {
  return view ? { ...view, sec: { ...view.sec } } : null;
}

/** Tests only: drop all state WITHOUT sending. */
export function __resetEngagementForTests() {
  stopTicking();
  cancelScrollFrame();
  detachListeners();
  view = null;
  consentCheck = null;
}
